import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  isElectron,
  useApi,
  useUnreadNotificationCount,
  useWorkspaces,
} from '@fluxter/shared';
import { formatNotification, type NotificationItem } from '@/lib/format-notification';
import { navigateToNotificationHref } from '@/lib/notification-navigation';

/**
 * Desktop bridge for native notifications + dock badge. Mounted once
 * (in `WorkspaceShell`) for the lifetime of the signed-in session.
 *
 * What it does:
 *   1. Mirrors the existing `useUnreadNotificationCount` poller value
 *      onto the OS dock / launcher / taskbar badge via the preload
 *      bridge. Always runs.
 *   2. Detects new notification arrivals by watching the unread count
 *      delta. When count goes UP, fetches the latest inbox page and
 *      diffs against the highest-id we've already seen. Fires a
 *      native OS toast for each new item, capped at 3, with an
 *      "…and N more" aggregate toast for any overflow.
 *   3. Subscribes to OS-toast click events and routes them through
 *      `navigateToNotificationHref` — the same helper the in-app
 *      bell uses, so the deep-link behaviour is identical no matter
 *      where the click came from.
 *
 * Hard skip on web (`window.fluxter` is undefined). The browser would
 * have to use HTML5 Notification API + permission UX, which is a
 * different design and out of scope for this slice.
 *
 * First-launch behaviour: we deliberately DON'T toast for whatever
 * unread count was already non-zero when the app opened. A user who
 * comes back to 17 unread notifications doesn't need 17 OS toasts —
 * they need to open the bell. The hook initialises its baseline on
 * the first count it sees and only fires toasts for genuine new
 * arrivals afterwards.
 */
const TOAST_CAP = 3;
const NEW_ITEMS_FETCH_LIMIT = 10;

type NotificationsResponse = { data?: NotificationItem[] } | undefined;

interface DesktopBridge {
  setBadgeCount?: (count: number) => Promise<number>;
  showNotification?: (spec: {
    id: string;
    title: string;
    body: string;
    href?: unknown;
  }) => Promise<boolean>;
  onNotificationClick?: (
    callback: (payload: { id: string; href: unknown }) => void,
  ) => () => void;
}

const bridge = (): DesktopBridge | undefined => {
  if (!isElectron()) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).fluxter as DesktopBridge | undefined;
};

interface WorkspaceListItem {
  id: string;
  attributes?: { slug?: string };
}

interface WorkspacesPayload {
  data?: WorkspaceListItem[];
}

const buildWorkspaceSlugMap = (data: unknown): ReadonlyMap<string, string> => {
  const items = (data as WorkspacesPayload | undefined)?.data ?? [];
  const map = new Map<string, string>();
  for (const w of items) {
    if (w.attributes?.slug) map.set(w.id, w.attributes.slug);
  }
  return map;
};

export const useDesktopNotifications = (): void => {
  const api = useApi();
  const navigate = useNavigate();
  const unreadCount = useUnreadNotificationCount();
  const workspaces = useWorkspaces();

  const workspaceSlugById = useMemo(
    () => buildWorkspaceSlugMap(workspaces.data),
    [workspaces.data],
  );

  // Track the previous unread count so we can detect deltas. `null`
  // means "never observed yet" — the first observation initialises
  // the baseline without firing any toasts.
  const lastUnreadRef = useRef<number | null>(null);
  // Highest notification id we've already shown a toast for. ULIDs
  // sort lexicographically by creation time, so a `>` comparison
  // gives us "newer than last seen".
  const highestSeenIdRef = useRef<string | null>(null);
  // Whether we've initialised `highestSeenIdRef` from the inbox.
  // First unread-count observation triggers a one-shot fetch to set
  // this so subsequent deltas have a baseline to diff against.
  const baselineInitialisedRef = useRef(false);

  // Push the unread count to the dock badge whenever it changes. No
  // delta gating here — the badge should always reflect the current
  // value even on first load, refresh-after-focus, etc.
  useEffect(() => {
    const b = bridge();
    if (!b?.setBadgeCount) return;
    const count = unreadCount.data;
    if (count === undefined) return;
    void b.setBadgeCount(count);
  }, [unreadCount.data]);

  // Clear the badge on unmount (e.g. logout, app close handled by OS
  // anyway). Otherwise a logged-out app would keep advertising the
  // last unread count to the dock until the user signed back in.
  useEffect(() => {
    return () => {
      const b = bridge();
      if (b?.setBadgeCount) void b.setBadgeCount(0);
    };
  }, []);

  // Initialise the highest-seen-id baseline on first unread count
  // observation. Done once per app boot; avoids toasting for
  // notifications that already existed before the user opened the app.
  useEffect(() => {
    const b = bridge();
    if (!b?.showNotification) return;
    if (baselineInitialisedRef.current) return;
    if (unreadCount.data === undefined) return;

    baselineInitialisedRef.current = true;
    void api
      .GET('/v1/notifications', { params: { query: { limit: '1' } as never } })
      .then(({ data }) => {
        const items = (data as NotificationsResponse)?.data ?? [];
        highestSeenIdRef.current = items[0]?.id ?? null;
      })
      .catch(() => {
        // Network blip on baseline; leave highestSeenId at null and
        // accept that the very next delta will likely toast for
        // pre-existing items. Not worth a retry loop.
      });
  }, [api, unreadCount.data]);

  // Detect new arrivals via unread-count delta and fire toasts.
  useEffect(() => {
    const b = bridge();
    if (!b?.showNotification) return;
    const current = unreadCount.data;
    if (current === undefined) return;

    const previous = lastUnreadRef.current;
    lastUnreadRef.current = current;

    if (previous === null) return; // baseline observation; handled above
    if (current <= previous) return; // count went down or stayed; nothing new

    // Wait until baseline is established before notifying — otherwise
    // a fast second poll could outrace the baseline fetch and toast
    // for old items.
    if (highestSeenIdRef.current === null && baselineInitialisedRef.current === false) {
      return;
    }

    void api
      .GET('/v1/notifications', {
        params: { query: { limit: String(NEW_ITEMS_FETCH_LIMIT) } as never },
      })
      .then(async ({ data }) => {
        const items = (data as NotificationsResponse)?.data ?? [];
        const cutoff = highestSeenIdRef.current;
        const newItems = cutoff ? items.filter((i) => i.id > cutoff) : items;
        if (newItems.length === 0) return;

        // Update high-water mark to the newest item we just observed
        // (which is items[0] given ULID-desc sort). Do this BEFORE
        // firing toasts so a synchronous re-trigger doesn't double-fire.
        const newest = items[0]?.id;
        if (newest) highestSeenIdRef.current = newest;

        const visible = newItems.slice(0, TOAST_CAP);
        const overflow = newItems.length - visible.length;

        for (const item of visible) {
          const formatted = formatNotification(item, workspaceSlugById);
          const actor = item.attributes?.actor_name ?? 'Someone';
          const title = `${actor} ${formatted.verb}`.trim();
          const bodyParts = [formatted.parentLabel, formatted.excerpt].filter(
            (s): s is string => Boolean(s),
          );
          const body = bodyParts.join(' — ') || 'Open Fluxter to view';
          await b.showNotification?.({
            id: item.id,
            title,
            body,
            href: formatted.href,
          });
        }

        if (overflow > 0) {
          await b.showNotification?.({
            id: `aggregate:${Date.now()}`,
            title: `…and ${overflow} more`,
            body: 'Open Fluxter to see all new notifications',
          });
        }
      })
      .catch(() => {
        // If the inbox fetch fails, the badge still reflects the new
        // count (handled by the other effect) — only the toast UX is
        // degraded for this delta.
      });
  }, [api, unreadCount.data, workspaceSlugById]);

  // Subscribe to OS-toast click events and route through the same
  // navigation helper the in-app bell uses.
  useEffect(() => {
    const b = bridge();
    if (!b?.onNotificationClick) return;
    return b.onNotificationClick((payload) => {
      // Defensive: `href` is `unknown` over the IPC boundary; verify
      // shape before handing to the typed helper.
      if (!payload.href || typeof payload.href !== 'object') return;
      const href = payload.href as { kind?: string };
      if (typeof href.kind !== 'string') return;
      // Cast is safe — the renderer authored this object originally
      // and main echoed it verbatim.
      void navigateToNotificationHref(
        navigate,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload.href as any,
      );
    });
  }, [navigate]);
};
